import AddIcon from '@mui/icons-material/Add';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import {
  Box,
  Chip,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { REASONING_EFFORTS, ReasoningEffort } from '../../utils/axios/api';
import {
  chatEntryContextTitle,
  parseChatEntryContext,
  searchWithoutEntryContext,
} from '../../utils/chat-entry-context/chat-entry-context';
import {
  getStoredReasoningEffort,
  setStoredReasoningEffort,
} from '../../utils/reasoning-effort';
import { ChatAttachedFile, ChatInputProps } from './chat-input.interface';

export function ChatInputComponent({
  value,
  onChange,
  onSend,
  onStop,
  disabled,
  isStreaming,
  awaitingUserAction,
}: ChatInputProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const entryContext = useMemo(
    () => parseChatEntryContext(location.search),
    [location.search],
  );
  const clearEntryContext = useCallback(() => {
    navigate(
      {
        pathname: location.pathname,
        search: searchWithoutEntryContext(location.search),
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate]);
  const fileInputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState('');
  const [reasoningEffort, setReasoningEffort] = useState(getStoredReasoningEffort);
  const canSend = useMemo(
    () => text.trim().length > 0 && !disabled && !isStreaming,
    [text, disabled, isStreaming],
  );

  const handleReasoningChange = useCallback(
    (event: SelectChangeEvent<ReasoningEffort>) => {
      const nextValue = event.target.value as ReasoningEffort;
      setReasoningEffort(nextValue);
      setStoredReasoningEffort(nextValue);
    },
    [],
  );

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFilesChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const selected = event.target.files;

      if (!selected?.length) {
        return;
      }

      const nextFiles: ChatAttachedFile[] = Array.from(selected).map((file) => ({
        id: `${file.name}-${file.size}-${file.lastModified}-${crypto.randomUUID()}`,
        file,
      }));

      onChange([...value, ...nextFiles]);
      event.target.value = '';
    },
    [onChange, value],
  );

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (isStreaming) {
        onStop?.();
        return;
      }

      const nextText = text.trim();

      if (!nextText || disabled) {
        return;
      }

      onSend?.(nextText);
      setText('');
    },
    [disabled, isStreaming, onSend, onStop, text],
  );

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {awaitingUserAction ? (
        <Box
          role="status"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minHeight: 40,
            px: 1.75,
            borderRadius: '12px',
            bgcolor: 'grey.200',
            color: 'text.primary',
          }}
        >
          <HourglassEmptyOutlinedIcon
            sx={{ fontSize: 18, color: 'text.secondary' }}
          />
          <Typography variant="body2">
            {t('chatInput.waitingForAction')}
          </Typography>
        </Box>
      ) : null}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          minHeight: 108,
          borderRadius: '16px',
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
        }}
      >
        <input
          id={fileInputId}
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          disabled={disabled || isStreaming}
          onChange={handleFilesChange}
        />
        <InputBase
          fullWidth
          multiline
          maxRows={8}
          value={text}
          disabled={disabled || isStreaming}
          placeholder={t('chatInput.placeholder')}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) {
              return;
            }

            event.preventDefault();
            event.currentTarget.form?.requestSubmit();
          }}
          inputProps={{ 'aria-label': t('chatInput.placeholder') }}
          sx={{
            flex: 1,
            px: 1.75,
            pt: 1.5,
            alignItems: 'flex-start',
            fontSize: 15,
            '& textarea': {
              lineHeight: 1.5,
            },
            '& .MuiInputBase-input::placeholder': {
              color: 'text.secondary',
              opacity: 1,
            },
          }}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pl: 0.75,
            pr: 1,
            pb: 1,
            pt: 0.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              minWidth: 0,
              flex: 1,
            }}
          >
            <IconButton
              type="button"
              size="small"
              disabled={disabled || isStreaming}
              aria-label={t('chatInput.attach')}
              onClick={handleAttachClick}
              sx={{
                flexShrink: 0,
                width: 32,
                height: 32,
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'action.hover',
                  color: 'text.primary',
                },
              }}
            >
              <AddIcon sx={{ fontSize: 20 }} />
            </IconButton>
            {entryContext ? (
              <Tooltip
                title={`entryId: ${entryContext.entryId}\nspaceId: ${entryContext.spaceId}`}
                slotProps={{
                  tooltip: { sx: { whiteSpace: 'pre-line' } },
                }}
              >
                <Chip
                  label={chatEntryContextTitle(entryContext)}
                  size="small"
                  disabled={disabled || isStreaming}
                  onDelete={
                    disabled || isStreaming ? undefined : clearEntryContext
                  }
                  aria-label={t('chatInput.removeEntryContext', {
                    name: chatEntryContextTitle(entryContext),
                  })}
                  sx={{
                    maxWidth: '100%',
                    '& .MuiChip-label': {
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                  }}
                />
              </Tooltip>
            ) : null}
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flexShrink: 0,
            }}
          >
            <Select
              value={reasoningEffort}
              onChange={handleReasoningChange}
              aria-label="reasoningEffort"
              MenuProps={{
                slotProps: {
                  paper: {
                    sx: { fontSize: 13 },
                  },
                },
              }}
              sx={{
                height: 32,
                fontSize: 13,
                color: 'text.secondary',
                bgcolor: 'transparent',
                boxShadow: 'none',
                '& fieldset': { border: 'none' },
                '&:hover fieldset': { border: 'none' },
                '&.Mui-focused fieldset': { border: 'none' },
                '& .MuiSelect-select': {
                  py: 0,
                  pl: 1,
                  pr: '22px !important',
                  bgcolor: 'transparent',
                },
                '& .MuiSelect-icon': {
                  right: 0,
                  fontSize: 18,
                  color: 'text.secondary',
                },
                '&:hover': {
                  bgcolor: 'transparent',
                  color: 'text.primary',
                },
              }}
            >
              {REASONING_EFFORTS.map((effort) => (
                <MenuItem
                  key={effort}
                  value={effort}
                  dense
                  sx={{ fontSize: 13, minHeight: 32 }}
                >
                  {effort}
                </MenuItem>
              ))}
            </Select>
            <IconButton
              type="submit"
              disabled={isStreaming ? !onStop : !canSend}
              aria-label={t(isStreaming ? 'chatInput.stop' : 'chatInput.send')}
              sx={{
                width: 32,
                height: 32,
                bgcolor: isStreaming ? 'error.main' : 'primary.main',
                color: isStreaming ? 'error.contrastText' : 'primary.contrastText',
                '&:hover': {
                  bgcolor: isStreaming ? 'error.dark' : 'primary.dark',
                },
                '&.Mui-disabled': {
                  bgcolor: 'action.disabledBackground',
                  color: 'action.disabled',
                },
              }}
            >
              {isStreaming ? (
                <StopIcon sx={{ fontSize: 18 }} />
              ) : (
                <SendIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
